import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from '@/contexts/AuthContext';
import { SessionProvider } from '@/contexts/SessionContext';
import { Toaster } from '@/components/ui/sonner';

import LandingPage from '@/pages/LandingPage';
import AuthPage from '@/pages/AuthPage';
import CustomerDashboard from '@/pages/CustomerDashboard';
import CreateJobPage from '@/pages/CreateJobPage';
import JobDetailPage from '@/pages/JobDetailPage';
import AddAddressPage from '@/pages/AddAddressPage';
import AddressManagementPage from '@/pages/AddressManagementPage';
import ProfileSettings from '@/pages/ProfileSettings';
import TestPayment from '@/pages/TestPayment';
import ContractorDashboard from '@/pages/ContractorDashboard';
import ContractorOnboarding from '@/pages/ContractorOnboarding';
import BecomeContractorPage from '@/pages/BecomeContractorPage';
import AdminDashboard from '@/pages/AdminDashboard';
import AdminPanel from '@/pages/AdminPanel';
import MessagesPage from '@/pages/MessagesPage';
import PricingExplainerPage from '@/pages/PricingExplainerPage';
// New Dashboard Pages
import ProfilePage from '@/pages/ProfilePage';
import SettingsPage from '@/pages/SettingsPage';
import MyJobsPage from '@/pages/MyJobsPage';
import ContractorEarningsPage from '@/pages/ContractorEarningsPage';

// Admin roles that have access to the admin panel
const ADMIN_ROLES = ['admin', 'super_admin', 'support_agent', 'verification_reviewer', 'ops_dispatch', 'finance', 'support'];

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#007AFF] border-t-transparent mb-4"></div>
        <p className="text-neutral-600">Loading...</p>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" />;
  }

  // Use activeRole for routing (supports role switching)
  const activeRole = user.active_role || user.role?.toLowerCase?.() || user.role;
  const primaryRole = user.primary_role || activeRole;
  
  // Check role permissions
  if (allowedRoles) {
    const normalizedAllowed = allowedRoles.map(r => r.toLowerCase());
    const hasAccess = normalizedAllowed.includes(activeRole) || 
                      normalizedAllowed.includes(primaryRole) ||
                      // Super admins bypass all role restrictions
                      primaryRole === 'super_admin' ||
                      // Admin-level users can access customer routes
                      (normalizedAllowed.includes('customer') && ADMIN_ROLES.includes(primaryRole));
    
    if (!hasAccess) {
      return <Navigate to="/" />;
    }
  }

  return children;
};

function AppRoutes() {
  const { user, activeRole, primaryRole, hasAdminAccess } = useAuth();
  
  // Determine where to redirect based on ACTIVE role (not primary)
  const getHomeRoute = () => {
    if (!user) return '/';
    
    const role = activeRole || primaryRole || user.role;
    const normalizedRole = typeof role === 'string' ? role.toLowerCase() : role;
    
    // Super admins go to admin panel by default
    if (normalizedRole === 'super_admin') return '/admin';
    
    // Admin roles go to admin
    if (ADMIN_ROLES.includes(normalizedRole)) return '/admin';
    
    // Contractor mode
    if (normalizedRole === 'contractor') return '/contractor/dashboard';
    
    // Default to customer dashboard
    return '/dashboard';
  };

  return (
    <Routes>
      <Route path="/" element={user ? <Navigate to={getHomeRoute()} /> : <LandingPage />} />
      <Route path="/auth" element={!user ? <AuthPage /> : <Navigate to={getHomeRoute()} />} />
      
      {/* Customer Routes - Also accessible by admins in customer mode */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedRoles={['customer', 'super_admin', 'admin']}>
          <CustomerDashboard />
        </ProtectedRoute>
      } />
      <Route path="/jobs" element={
        <ProtectedRoute allowedRoles={['customer']}>
          <MyJobsPage />
        </ProtectedRoute>
      } />
      <Route path="/create-job" element={
        <ProtectedRoute allowedRoles={['customer']}>
          <CreateJobPage />
        </ProtectedRoute>
      } />
      <Route path="/addresses/new" element={
        <ProtectedRoute>
          <AddAddressPage />
        </ProtectedRoute>
      } />
      <Route path="/addresses" element={
        <ProtectedRoute allowedRoles={['customer']}>
          <AddressManagementPage />
        </ProtectedRoute>
      } />
      
      {/* Public Pages */}
      <Route path="/how-pricing-works" element={<PricingExplainerPage />} />
      
      {/* Shared Routes (Customer + Contractor) */}
      <Route path="/profile" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/profile/:userId" element={
        <ProtectedRoute>
          <ProfilePage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      <Route path="/settings-legacy" element={
        <ProtectedRoute>
          <ProfileSettings />
        </ProtectedRoute>
      } />
      <Route path="/test-payment" element={
        <ProtectedRoute>
          <TestPayment />
        </ProtectedRoute>
      } />
      <Route path="/jobs/:id" element={
        <ProtectedRoute>
          <JobDetailPage />
        </ProtectedRoute>
      } />
      
      {/* Messaging Routes */}
      <Route path="/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/messages/:threadId" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      <Route path="/messages/job/:jobId" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      
      {/* Legacy route */}
      <Route path="/jobs/:jobId/messages" element={
        <ProtectedRoute>
          <MessagesPage />
        </ProtectedRoute>
      } />
      
      {/* Contractor Routes */}
      <Route path="/contractor/dashboard" element={
        <ProtectedRoute allowedRoles={['contractor']}>
          <ContractorDashboard />
        </ProtectedRoute>
      } />
      {/* Contractor Onboarding - accessible by both customers and contractors */}
      <Route path="/contractor/onboarding" element={
        <ProtectedRoute allowedRoles={['customer', 'contractor']}>
          <ContractorOnboarding />
        </ProtectedRoute>
      } />
      {/* Become a Contractor - Public landing page */}
      <Route path="/become-contractor" element={<BecomeContractorPage />} />
      <Route path="/pro" element={<BecomeContractorPage />} />
      <Route path="/contractors" element={<BecomeContractorPage />} />
      <Route path="/contractor/jobs" element={
        <ProtectedRoute allowedRoles={['contractor']}>
          <MyJobsPage />
        </ProtectedRoute>
      } />
      <Route path="/contractor/earnings" element={
        <ProtectedRoute allowedRoles={['contractor']}>
          <ContractorEarningsPage />
        </ProtectedRoute>
      } />
      <Route path="/contractor/live-jobs" element={
        <ProtectedRoute allowedRoles={['contractor']}>
          <ContractorDashboard />
        </ProtectedRoute>
      } />
      <Route path="/contractor/certifications" element={
        <ProtectedRoute allowedRoles={['contractor']}>
          <ContractorOnboarding />
        </ProtectedRoute>
      } />
      
      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminPanel />
        </ProtectedRoute>
      } />
      <Route path="/admin/legacy" element={
        <ProtectedRoute allowedRoles={ADMIN_ROLES}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <SessionProvider>
          <AppRoutes />
          <Toaster position="top-center" richColors />
        </SessionProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
